import axios from "axios"
import { isObjectV2 } from "Utils/global"
import { config } from "Utils/config"
import { IPropsNotification, INotification } from "./interfaces/notification-interface"
import { getLocaleSuffix } from "Utils/translationLoader"

export async function getUserDataNotifications(accessToken: string) {
  const requestURL = `${config.APOLLO_SERVER_URL}/notification/user-profil`

  try {
    const response = await axios.get(requestURL, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (response?.data) {
      return response.data
    }
  } catch (err) {
    console.error(`[route] => /notifications/user-data => ERROR GET => `, err)
  }
}

export async function patchUserDataNotifications(payload: any, accessToken: string) {
  const requestURL = `${config.APOLLO_SERVER_URL}/notification/user-profil`

  try {
    const response = await axios.post(
      requestURL,
      {
        ...payload,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )

    if (response.data) {
      return response.data
    }
  } catch (err) {
    console.error(`[route] => /notifications/user-data => ERROR PATCH => `, err)
  }
}

const getInfoFromUTCDate = (utcDate: string) => getInfoFromDateObj(new Date(utcDate))

const getInfoFromDateObj = (dateObj: Date) => {
  return [
    dateObj.getFullYear(),
    dateObj.getMonth() + 1,
    dateObj.getDate(),
    dateObj.getHours(),
    dateObj.getMinutes(),
    dateObj.getSeconds(),
  ]
}

const getInfoToDisplayFromUTCDate = (utcDate: string) => {
  const infos = getInfoFromUTCDate(utcDate)
  return infos.map(info => {
    const stringifiedInfo = `${info}`
    if (stringifiedInfo.length <= 1) return `0${info}`
    return `${info}`
  })
}

//UTC Date with a Z
const utcDateRegex = /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\.[0-9]{3}Z$/

const isUTCDate = (date: string | undefined): boolean =>
  typeof date === "string" && utcDateRegex.test(date)

export interface ITransformedDate {
  date: string
  hours?: string
}

export const transformDate = (date: string | undefined): ITransformedDate => {
  if (!date) {
    return { date: "" }
  }

  if (isUTCDate(date)) {
    const [year, month, day, hours, minutes] = getInfoToDisplayFromUTCDate(date)
    return {
      date: `${day}/${month}/${year}`,
      hours: `${hours}:${minutes}`,
    }
  }

  const [rawDate, rawTime] = date.includes("T") ? date.split("T") : date.split(" ")
  const [year, month, day] = rawDate?.split("-") ?? []
  const [hours, minutes] = rawTime?.split(":") ?? []

  if (!year || !month || !day) {
    return { date }
  }

  return {
    date: `${day}/${month}/${year}`,
    ...(hours ? { hours: `${hours}:${minutes ?? "00"}` } : {}),
  }
}

export const generateNotification = ({
  currentUser,
  usersIdsToInform,
  eventString,
  elementInBold,
  redirectUrl,
  redirectState,
  channelId,
}: IPropsNotification): INotification | null => {
  const localeSuffix = getLocaleSuffix(currentUser?.LANGUAGE_ID)
  let error: string | null = null
  if (!channelId) error = "None channel match with this channelAssociate"
  else if (!currentUser) error = "Current user is mandatory"
  else if (!Array.isArray(usersIdsToInform)) error = "Users ids to inform should be an array"
  else if (typeof eventString !== "string") error = "EventString should be a string"

  if (error) {
    console.warn(`📢 Error notification: ${error}`)
    return null
  }
  return {
    to_principals: usersIdsToInform?.map(id => `user:${id}`),
    data: {
      event: eventString,
      elementInBold: elementInBold ? elementInBold : "",
      redirectUrl: redirectUrl ? redirectUrl : "",
      byUser: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
      infos: ``,
      email: {
        url: "https://pocketstudio.pocketresult.com/",
        event: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME} ${eventString}`,
      },
      redirect_state: isObjectV2(redirectState) ? { ...redirectState } : {},
    },
    ephemeral: false,
    channel_id: `${channelId}_${localeSuffix}`,
  }
}

export const generateTemplateNotification = (nameTemplate: string, params: any) => {
  const notif = params?.notif || {}
  const trad = params?.trad || {}
  const currentUser = params?.currentUser || {}
  const payload = params?.payload || {}
  switch (nameTemplate) {
    // ===== Notification Dashboard =====
    // Modification d'un Dashboard
    case "dashboard_update":
      return {
        ...notif,
        name_template: "dashboard_update",
        event_template: `<b>{{assignateurName}}</b> ${trad?.A_MODIFIE_LA_PAGE} <b>{{titlePage}}</b>`,
        redirectUrl_template: `/page/{{idRedirect}}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          titlePage: `${payload?.title}`,
          idRedirect: `${payload?._id?.$oid}`,
        },
      }

    // Suppression d'un Dashboard
    case "dashboard_delete":
      return {
        ...notif,
        name_template: "dashboard_delete",
        event_template: `<b>{{assignateurName}}</b> ${trad?.A_SUPPRIME_LA_PAGE} <b>{{titlePage}}</b>`,
        redirectUrl_template: "",
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          titlePage: `${payload?.page.title}`,
        },
      }

    // ===== Notification Recommendation =====
    // Ajout de nouveaux fichiers d'une recommandation
    case "control_assignment_file":
      return {
        ...notif,
        name_template: "control_assignment_file",
        event_template: `<b>{{assignateurName}}</b> ${trad?.ADD_DOCUMENTS_TO_RECOMMENDATION} <b>{{recoName}}</b> ${trad?.BELONGING_TO_THE_CONTROL} <b>{{controlName}}</b>`,
        redirectUrl_template: `/recommendation`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          recoName: `${payload?.recoName}`,
          controlName: `${payload?.controlName}`,
        },
      }

    // ===== Notification Non Conformity =====
    // Création de la non-conformité
    case "anomaly_complication_created":
      return {
        ...notif,
        name_template: "anomaly_complication_created",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{nonConformityName}}</b>`,
        redirectUrl_template: `/nonConformity-details/{{nonConformityType}}/{{newNonConformityId}}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          nonConformityName: `${notif?.data?.elementInBold}`,
          newNonConformityId: `${payload?.newNonConformityId}`,
          nonConformityType: `${payload?.nonConformityType}`,
        },
      }
    case "incident_complication_created":
      return {
        ...notif,
        name_template: "complication_created",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{nonConformityName}}</b>`,
        redirectUrl_template: `/nonConformity-details/{{nonConformityType}}/{{newNonConformityId}}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          nonConformityName: `${notif?.data?.elementInBold}`,
          newNonConformityId: `${payload?.newNonConformityId}`,
          nonConformityType: `${payload?.nonConformityType}`,
        },
      }

    // Informations des taches assignés
    case "complication_assigned_tasks":
      return {
        ...notif,
        name_template: "complication_assigned_tasks",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{nonConformityName}}</b>`,
        redirectUrl_template: `/nonConformity-details/{{nonConformityType}}/{{newNonConformityId}}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          nonConformityName: `${notif?.data?.elementInBold}`,
          newNonConformityId: `${payload?.newNonConformityId}`,
          nonConformityType: `${payload?.nonConformityType}`,
        },
      }

    // Mise à jour du Statut
    case "complication_patch_statut":
      return {
        ...notif,
        name_template: "complication_patch_statut",
        event_template: `<b>{{assignateurName}}</b> ${trad?.UPDATED_STATUS_NON_CONFORMITY_TO} ${trad?.FROM} <b>{{translatedInitialStatus}}</b> ${trad?.TO} <b>{{translatedNewStatus}}</b> concernant <b>{{nonConformityName}}</b>`,
        redirectUrl_template: `/nonConformity-details/{{type}}/{{nonConformityId}}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          nonConformityName: `${notif?.data?.elementInBold}`,
          type: `${payload?.nonConformity?.type}`,
          nonConformityId: `${payload?.nonConformityId}`,
          translatedInitialStatus: `${payload?.nonConformity?.translatedInitialStatus}`,
          translatedNewStatus: `${payload?.nonConformity?.translatedNewStatus}`,
        },
      }

    // Validation des tâches
    case "validation_action_plan_completed":
      return {
        ...notif,
        name_template: "validation_action_plan_completed",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{actionPlanName}}</b>`,
        redirectUrl_template: `/nonConformity-details/{{type}}/{{nonConformityId}}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          type: `${payload?.nonConformity?.type}`,
          nonConformityId: `${payload?._id?.$oid}`,
        },
      }

    // ===== Notification Audit =====
    // Création recommendation audit
    case "audit_complication_created":
      return {
        ...notif,
        name_template: "audit_complication_created",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{nonConformityName}}</b>`,
        redirectUrl_template: `/audit-details/{{auditId}}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          nonConformityName: `${notif?.data?.elementInBold}`,
          auditId: `${payload?.auditId}`,
        },
      }

    // ===== Notification Control Plan =====
    // Notification User Affected to Control Plan
    case "control_plan_assignment":
      return {
        ...notif,
        name_template: "control_plan_assignment",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{controlPlanName}}</b>`,
        redirectUrl_template: `/controlPlans/${params?.newId || ""}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          controlPlanName: `${notif?.data?.elementInBold}`,
          controlPlanId: `${params?.newId}`,
        },
      }
    case "control_assignment":
      return {
        ...notif,
        name_template: "control_assignment",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{controlName}}</b>`,
        redirectUrl_template: "",
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          controlName: `${notif?.data?.elementInBold}`,
        },
      }
    case "confirm_control":
      return {
        ...notif,
        name_template: "confirm_control",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{controlName}}</b>`,
        redirectUrl_template: `/controlSynthese/{{controlId}}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          controlName: `${notif?.data?.elementInBold}`,
          controlId: `${payload?.controlId}`,
        },
      }
    case "control_message_thread":
      return {
        ...notif,
        name_template: "control_message_thread",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{controlName}}</b>`,
        redirectUrl_template: `/planningControls`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          controlName: `${notif?.data?.elementInBold}`,
          responseId: `${notif.redirect_state?.responseId}`,
        },
      }
    case "validate_control":
      return {
        ...notif,
        name_template: "validate_control",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{controlName}}</b>`,
        redirectUrl_template: `/planningControls`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          controlName: `${notif?.data?.elementInBold}`,
          responseId: `${notif.redirect_state?.responseId}`,
        },
      }
    case "reject_control":
      return {
        ...notif,
        name_template: "reject_control",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{controlName}}</b>`,
        redirectUrl_template: `/planningControls`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          controlName: `${notif?.data?.elementInBold}`,
          responseId: `${notif.redirect_state?.responseId}`,
        },
      }
    case "control_response":
      return {
        ...notif,
        name_template: "control_response",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{controlName}}</b>`,
        redirectUrl_template: `/planningControls`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          controlName: `${notif?.data?.elementInBold}`,
          responseId: `${notif.redirect_state?.responseId}`,
        },
      }
    // ===== Notification Pages =====
    // Création d'une page
    case "page_creation":
      return {
        ...notif,
        name_template: "page_creation",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{pageName}}</b>`,
        redirectUrl_template: `/page/{{pageId}}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          pageName: `${notif?.data?.elementInBold}`,
          pageId: `${payload?.pageId}`,
        },
      }
    // ===== MSF =====
    // Création d'une Affaire
    case "msf_creation_affaire":
      return {
        ...notif,
        name_template: "msf_creation_affaire",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{affaireReference}}</b>`,
        redirectUrl_template: `/affaires`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          affaireReference: `${notif?.data?.elementInBold}`,
        },
      }
    // Modification d'une Affaire
    case "msf_modification_affaire":
      return {
        ...notif,
        name_template: "msf_modification_affaire",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{affaireReference}}</b>`,
        redirectUrl_template: `/fiche-affaire/{{idAffaire}}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          affaireReference: `${notif?.data?.elementInBold}`,
          idAffaire: `${payload?.idAffaire}`,
        },
      }
    // Ajout d'un document à une affaire
    case "msf_add_document_affaire":
      return {
        ...notif,
        name_template: "msf_add_document_affaire",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{affaireReference}}</b>`,
        redirectUrl_template: `/fiche-affaire/{{idAffaire}}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          affaireReference: `${notif?.data?.elementInBold}`,
          idAffaire: `${payload?.idAffaire}`,
        },
      }
    // Validation planning travaux
    case "msf_validation_planning_travaux":
      return {
        ...notif,
        name_template: "msf_validation_planning_travaux",
        event_template: `<b>{{assignateurName}}</b> ${notif?.data?.event} <b>{{affaireReference}}</b>`,
        redirectUrl_template: `/fiche-affaire/{{idAffaire}}`,
        variables: {
          assignateurName: `${currentUser.FIRSTNAME} ${currentUser.LASTNAME}`,
          affaireReference: `${notif?.data?.elementInBold}`,
          idAffaire: `${payload?.idAffaire}`,
        },
      }
    default:
      console.warn(`📢 Error notification: Template ${nameTemplate} not found`)
      return null
  }
}
